/**
 * Reusable chart frame with themed legends, accessible details, state handling, and resize notifications.
 * @element qds-chart
 * @attr {string} title-text - Accessible chart heading.
 * @attr {string} description - Chart purpose and interaction instructions.
 * @attr {boolean} loading - Show a loading state and hide the plot.
 * @attr {string} error - Accessible error message; hides stale plot content.
 * @attr {string} empty-message - Empty-state message; hides the plot when supplied.
 * @property {Array} series - Named series with stable id, label, and optional CSS color.
 * @property {string} activeSeriesId - Highlighted series ID, or empty for all series.
 * @property {Object} tooltip - Plain-text title and lines for the shared hover details region, or null.
 * @fires {CustomEvent} qds-chart-resize - Plot viewport changed; detail contains width and height.
 * @fires {CustomEvent} qds-series-focus - Legend selection changed; detail contains seriesId or null.
 * @csspart container - Chart frame.
 * @csspart heading - Chart title.
 * @csspart description - Descriptive text.
 * @csspart legend - Series legend.
 * @csspart plot - Responsive plot viewport.
 * @csspart tooltip - Shared text-only hover details.
 * @csspart status - Loading, empty, and error messages.
 * @csspart details - Accessible selected-data region.
 * @slot plot - SVG or another chart renderer owned by the consuming component.
 * @slot tools - Chart-specific controls.
 * @slot details - Accessible selected-data detail or table.
 */
import { loadBaseCSS } from '../shared/button-utils.js';
import { chartColor, chartSeries, type QdsChartSeries, type QdsChartTooltip } from './chart-colors.js';

export class QdsChart extends HTMLElement {
  private _series: QdsChartSeries[] = [];
  private _activeSeriesId = '';
  private _tooltip: QdsChartTooltip | null = null;
  private _observer?: ResizeObserver;
  private _legendSignature = '';
  private _onLegendClick = (event: Event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('button[data-series]');
    if (!button) return;
    this.activeSeriesId = this._activeSeriesId === button.dataset.series ? '' : button.dataset.series!;
    this.dispatchEvent(new CustomEvent('qds-series-focus', { bubbles: true, composed: true, detail: { seriesId: this._activeSeriesId || null } }));
  };

  static get observedAttributes(): string[] { return ['title-text', 'description', 'loading', 'error', 'empty-message']; }
  constructor() { super(); this.attachShadow({ mode: 'open' }); loadBaseCSS(); }
  get series(): QdsChartSeries[] { return this._series.map(item => ({ ...item })); }
  set series(value: QdsChartSeries[]) { this._series = chartSeries(value); this._render(); }
  get activeSeriesId(): string { return this._activeSeriesId; }
  set activeSeriesId(value: string) { this._activeSeriesId = String(value || ''); this._render(); }
  get tooltip(): QdsChartTooltip | null { return this._tooltip ? { title: this._tooltip.title, lines: [...this._tooltip.lines] } : null; }
  set tooltip(value: QdsChartTooltip | null) {
    this._tooltip = value ? { title: String(value.title), lines: value.lines.map(String) } : null;
    this._renderTooltip();
  }
  connectedCallback(): void {
    this._render();
    this.shadowRoot!.addEventListener('click', this._onLegendClick);
    if (typeof ResizeObserver !== 'undefined') {
      this._observer = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect;
        this.dispatchEvent(new CustomEvent('qds-chart-resize', { bubbles: true, composed: true, detail: { width, height } }));
      });
      this._observer.observe(this.shadowRoot!.querySelector('[part="plot"]')!);
    }
  }
  disconnectedCallback(): void { this._observer?.disconnect(); this.shadowRoot!.removeEventListener('click', this._onLegendClick); }
  attributeChangedCallback(): void { this._render(); }

  private _render(): void {
    if (!this.isConnected) return;
    const root = this.shadowRoot!;
    if (!root.querySelector('[part="container"]')) root.innerHTML = `
      <style>
        :host { display:block; color:var(--theme-text-primary,#333); font:var(--theme-font-size-md,14px)/1.5 var(--theme-font-family,'IBM Plex Sans',sans-serif); }
        [hidden] { display:none !important; }
        [part="container"] { min-width:0; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:var(--theme-radius-md,8px); padding:var(--theme-space-4,16px); background:var(--theme-background-primary,#fff); }
        h2 { margin:0; font-size:var(--theme-font-size-lg,18px); }
        p { margin:var(--theme-space-2,8px) 0; }
        [part="description"] { color:var(--theme-text-secondary,#555); }
        [part="legend"] { display:flex; flex-wrap:wrap; gap:var(--theme-space-2,8px); margin:var(--theme-space-3,12px) 0; }
        button { display:flex; align-items:center; gap:8px; min-height:44px; padding:8px; border:1px solid transparent; border-radius:var(--theme-radius-sm,4px); color:inherit; font:inherit; background:transparent; cursor:pointer; }
        button:hover { background:var(--theme-background-hover,rgba(0,0,0,.05)); }
        button[aria-pressed="true"] { border-color:var(--theme-accent-primary,#445E89); }
        button:focus-visible { outline:2px solid var(--theme-accent-primary,#445E89); outline-offset:2px; }
        .swatch { width:12px; height:12px; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:2px; flex:none; }
        [part="plot"] { width:100%; min-width:0; }
        ::slotted(svg) { display:block; width:100%; }
        [part="tooltip"] { min-height:3em; padding:var(--theme-space-3,12px); background:var(--theme-background-secondary,#f8f8f8); border-radius:var(--theme-radius-sm,4px); font-variant-numeric:tabular-nums; }
        [part="tooltip"] ul { margin:4px 0 0; padding-inline-start:20px; }
        [part="status"] { padding:var(--theme-space-6,24px); }
      </style>
      <section part="container" aria-labelledby="chart-title" aria-describedby="chart-description">
        <h2 part="heading" id="chart-title"></h2><p part="description" id="chart-description"></p>
        <div part="tools"><slot name="tools"></slot></div>
        <div part="legend" role="group" aria-label="Highlight chart series"></div>
        <p part="status" role="status"></p>
        <div part="plot"><slot name="plot"></slot></div>
        <div part="tooltip" role="tooltip" id="chart-tooltip" hidden></div>
        <div part="details"><slot name="details"></slot></div>
      </section>`;
    root.querySelector('h2')!.textContent = this.getAttribute('title-text') || 'Chart';
    root.querySelector('[part="description"]')!.textContent = this.getAttribute('description') || '';
    const loading = this.hasAttribute('loading');
    const error = this.getAttribute('error');
    const status = root.querySelector<HTMLElement>('[part="status"]')!;
    const message = error || (loading ? 'Loading chart…' : this.getAttribute('empty-message') || '');
    status.textContent = message; status.hidden = !message; status.setAttribute('role', error ? 'alert' : 'status');
    root.querySelector('[part="container"]')!.setAttribute('aria-busy', String(loading));
    for (const part of ['plot', 'legend', 'tools', 'details']) root.querySelector<HTMLElement>(`[part="${part}"]`)!.hidden = !!message;
    const signature = JSON.stringify(this._series);
    if (signature !== this._legendSignature) {
      this._legendSignature = signature;
      const buttons = this._series.map(series => {
        const button = document.createElement('button'); button.type = 'button'; button.dataset.series = series.id;
        button.setAttribute('aria-label', `Highlight ${series.label}`);
        const swatch = document.createElement('span'); swatch.className = 'swatch'; swatch.style.backgroundColor = chartColor(series); swatch.setAttribute('aria-hidden', 'true');
        button.append(swatch, document.createTextNode(series.label)); return button;
      });
      root.querySelector('[part="legend"]')!.replaceChildren(...buttons);
    }
    root.querySelectorAll<HTMLButtonElement>('[data-series]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.series === this._activeSeriesId)));
    this._renderTooltip();
  }
  private _renderTooltip(): void {
    const region = this.shadowRoot!.querySelector<HTMLElement>('[part="tooltip"]'); if (!region) return;
    region.hidden = !this._tooltip || this.hasAttribute('loading') || !!this.getAttribute('error') || !!this.getAttribute('empty-message');
    region.replaceChildren(); if (!this._tooltip) return;
    const heading = document.createElement('strong'); heading.textContent = this._tooltip.title;
    const list = document.createElement('ul');
    this._tooltip.lines.forEach(line => { const item = document.createElement('li'); item.textContent = line; list.append(item); });
    region.append(heading, list);
  }
}
if (!customElements.get('qds-chart')) customElements.define('qds-chart', QdsChart);
